// src/lib/dataService.js
// Tutte le operazioni Firestore in un unico posto.

import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc,
  deleteDoc, query, where, orderBy, serverTimestamp, writeBatch
} from 'firebase/firestore';
import { db } from './firebase';
import { addYears, addMonths, addDays } from 'date-fns';

// ── COSTANTI ─────────────────────────────────────────────
export const CATEGORIES  = ['AUTO', 'MOTO', 'ALTRO'];
export const DEADLINES   = ['bollo', 'assicurazione', 'collaudo', 'messa_in_moto', 'certificazione'];
export const RECURRENCES = [
  { value: '6m',   label: '6 mesi' },
  { value: '1y',   label: '1 anno' },
  { value: '2y',   label: '2 anni' },
  { value: '3y',   label: '3 anni' },
  { value: 'free', label: 'Data libera' },
];

export const BOLLO_ESENZIONE_ANNI = 30;

export function isBolloEsente(annoImmatricolazione) {
  if (!annoImmatricolazione) return false;
  return (new Date().getFullYear() - annoImmatricolazione) >= BOLLO_ESENZIONE_ANNI;
}

export function calcolaScadenza(dataEsecuzione, ricorrenza) {
  const d = new Date(dataEsecuzione);
  if (ricorrenza === '6m') return addMonths(d, 6);
  if (ricorrenza === '1y') return addYears(d, 1);
  if (ricorrenza === '2y') return addYears(d, 2);
  if (ricorrenza === '3y') return addYears(d, 3);
  return null; // free = data libera, l'utente la inserisce manualmente
}

export function daysDiff(date) {
  if (!date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return Math.round((d - today) / (1000 * 60 * 60 * 24));
}

export function scadStatus(date) {
  const dd = daysDiff(date);
  if (dd === null) return 'none';
  if (dd < 0)      return 'danger';
  if (dd < 60)     return 'warn';
  return 'ok';
}

// ── SETTINGS ─────────────────────────────────────────────
export async function getSettings() {
  const snap = await getDoc(doc(db, 'settings', 'app'));
  if (snap.exists()) return snap.data();
  // Default se non esistono ancora
  return {
    tipi: {
      bollo:          { attivo: true,  ricorrenzaDefault: '1y' },
      assicurazione:  { attivo: true,  ricorrenzaDefault: '1y' },
      collaudo:       { attivo: true,  ricorrenzaDefault: '2y' },
      messa_in_moto:  { attivo: true,  ricorrenzaDefault: 'free' },
      certificazione: { attivo: false, ricorrenzaDefault: '2y' },
    },
    notifiche: {
      anticipo1: 30,
      anticipo2: 7,
      anticipo3: 1,
      email:   true,
      inApp:   true,
      push:    false,
    },
    permessi: {
      utentiModificanoScadenze: true,
      utentiAggiunganoVeicoli:  false,
      utentiVedonoAltri:        false,
    }
  };
}

export async function saveSettings(settings) {
  await updateDoc(doc(db, 'settings', 'app'), settings);
}

// ── UTENTI ───────────────────────────────────────────────
export async function getUsers() {
  const snap = await getDocs(collection(db, 'users'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function createUser(uid, data) {
  await writeBatch(db).set(doc(db, 'users', uid), {
    ...data,
    role: data.role || 'user',
    createdAt: serverTimestamp(),
  }).commit();
}

export async function getUserProfile(uid) {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// ── VEICOLI ───────────────────────────────────────────────
export async function getVehicles(userProfile) {
  let q;
  if (userProfile.role === 'admin') {
    q = query(collection(db, 'vehicles'), orderBy('marca'));
  } else {
    q = query(
      collection(db, 'vehicles'),
      where('ownerIds', 'array-contains', userProfile.id),
      orderBy('marca')
    );
  }
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function addVehicle(data) {
  return await addDoc(collection(db, 'vehicles'), {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateVehicle(id, data) {
  await updateDoc(doc(db, 'vehicles', id), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteVehicle(id) {
  await deleteDoc(doc(db, 'vehicles', id));
}

// ── SCADENZE ─────────────────────────────────────────────
export async function getDeadlines(vehicleId) {
  const snap = await getDocs(
    query(collection(db, 'vehicles', vehicleId, 'deadlines'), orderBy('dataScadenza'))
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getAllDeadlines(vehicleIds) {
  // Raccoglie scadenze di tutti i veicoli dell'utente
  const results = [];
  for (const vid of vehicleIds) {
    const snap = await getDocs(
      collection(db, 'vehicles', vid, 'deadlines')
    );
    snap.docs.forEach(d => results.push({ id: d.id, vehicleId: vid, ...d.data() }));
  }
  return results;
}

export async function addDeadline(vehicleId, data) {
  // data: { tipo, dataEsecuzione, dataScadenza, ricorrenza, note, attiva }
  return await addDoc(collection(db, 'vehicles', vehicleId, 'deadlines'), {
    ...data,
    attiva: data.attiva !== false,
    createdAt: serverTimestamp(),
  });
}

export async function updateDeadline(vehicleId, deadlineId, data) {
  await updateDoc(
    doc(db, 'vehicles', vehicleId, 'deadlines', deadlineId),
    { ...data, updatedAt: serverTimestamp() }
  );
}

export async function deleteDeadline(vehicleId, deadlineId) {
  await deleteDoc(doc(db, 'vehicles', vehicleId, 'deadlines', deadlineId));
}

// ── DOCUMENTI ALLEGATI ────────────────────────────────────
export async function getDocuments(vehicleId) {
  const snap = await getDocs(collection(db, 'vehicles', vehicleId, 'documents'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function addDocument(vehicleId, data) {
  // data: { tipo, nome, url, storageRef, uploadedAt }
  return await addDoc(collection(db, 'vehicles', vehicleId, 'documents'), {
    ...data,
    createdAt: serverTimestamp(),
  });
}

export async function deleteDocument(vehicleId, documentId) {
  await deleteDoc(doc(db, 'vehicles', vehicleId, 'documents', documentId));
}

// ── IMPORT DA EXCEL (per migrazione iniziale) ────────────
export async function importVehiclesFromArray(vehicles, ownerMap) {
  // ownerMap: { 'MARCO': 'uid_marco', 'MARTINA': 'uid_martina', ... }
  const batch = writeBatch(db);
  vehicles.forEach(v => {
    const ownerIds = (v.prop || []).map(p => ownerMap[p.toUpperCase()]).filter(Boolean);
    const ref = doc(collection(db, 'vehicles'));
    batch.set(ref, {
      categoria:         v.categoria,
      targa:             v.targa || null,
      marca:             v.marca,
      modello:           v.modello,
      annoImmatricolazione: v.annoImm || null,
      annoCostruzione:   v.annoCostr || null,
      proprietari:       v.prop || [],
      ownerIds,
      certificazione:    v.cert || null,
      assicuratore:      v.ass || null,
      libretto:          v.libretto || false,
      note:              v.note || '',
      createdAt:         serverTimestamp(),
    });
  });
  await batch.commit();
}
